import { FC, ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import GlobalSearch from './GlobalSearch';
import { useLockBodyScroll } from '@uidotdev/usehooks';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';

export type SearchContext = {
  scope: 'global' | 'project';
  project?: {
    name: string;
    iconImageUrl: string;
  };
};

interface GlobalSearchPopupProps {
  handleClose: () => void;
  searchContext?: SearchContext;
  searchForTerm: (term: string) => void;
}

const GlobalSearchPopup: FC<GlobalSearchPopupProps> = ({
  handleClose,
  searchContext,
  searchForTerm,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const animateClosed = () => setIsOpen(false);
  const screen = useRef<HTMLDivElement>(null);

  useLockBodyScroll();

  useGSAP(() => {
    if (isOpen) return;
    gsap.to(screen.current, {
      opacity: 0,
      duration: 0.15,
      ease: 'expo.in',
    });
  }, [isOpen]);

  return (
    <>
      {createPortal(
        <div
          ref={screen}
          className="grid fixed top-0 left-0 w-full h-full justify-center items-start page-side-padding pt-[12px] z-40 backdrop-blur-md bg-black/40 animate-fade-in-fast pointer-events-none"
        >
          <ClickScreen
            allowBackgroundClick={true}
            handleClose={animateClosed}
          />
          <Container isOpen={isOpen} handleClose={handleClose}>
            <GlobalSearch
              handleClose={animateClosed}
              searchContext={searchContext}
              searchForTerm={searchForTerm}
            />
          </Container>
        </div>,
        document.body
      )}
    </>
  );
};

const Container: FC<{
  children: ReactNode;
  isOpen: boolean;
  handleClose: () => void;
}> = ({ children, isOpen, handleClose }) => {
  const container = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (isOpen) {
      gsap.set(container.current, { height: 80 });
      gsap.to(container.current, {
        height: 'auto',
        duration: 0.4,
        delay: 0.2,
        ease: 'expo.inOut',
      });
    } else {
      gsap.to(container.current, {
        height: 80,
        opacity: 0.9,
        duration: 0.2,
        ease: 'expo.out',
        onComplete: handleClose,
      });
    }
  }, [isOpen]);

  return (
    <div
      ref={container}
      className="grid relative w-screen max-w-[616px] h-auto grid-flow-row gap-4 bg-gradient-to-b from-bionic-greyDark/90 via-[#03030590_85%] to-bionic-greyDark/50 bg-no-repeat rounded-[12px] border-[4px] border-[#3F3F3F] overflow-hidden pointer-events-auto"
    >
      {children}
    </div>
  );
};

const ClickScreen: FC<{
  allowBackgroundClick: boolean;
  handleClose: () => void;
}> = ({ allowBackgroundClick, handleClose }) => (
  <div
    className="block absolute top-0 left-0 w-full h-full pointer-events-auto"
    onClick={allowBackgroundClick ? handleClose : undefined}
  />
);

export default GlobalSearchPopup;
